import React, { useState, useEffect, useRef } from "react";
import { Box, Paper, Typography, TextField, IconButton, Button, Avatar, Zoom } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import ForumIcon from "@mui/icons-material/Forum";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import logoEmblem from "../assets/TowHelp logo-07.svg";

interface Message {
  id: number;
  text: string;
  sender: "bot" | "user";
  timestamp: Date;
}

const InteractiveChatbot: React.FC = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language.startsWith("ar");
  
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(1);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initial welcome message
  useEffect(() => {
    setMessages([
      {
        id: 1,
        text: isRTL 
          ? "مرحباً بك في TowHelp! أنا مساعدك الذكي هنا لمساعدتك في أي حالة طارئة على الطريق. كيف يمكنني مساعدتك اليوم؟"
          : "Welcome to TowHelp! I am your smart assistant, ready to help you with any roadside emergencies. How can I assist you today?",
        sender: "bot",
        timestamp: new Date()
      }
    ]);
  }, [isRTL]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const quickReplies = isRTL 
    ? [
        { key: "towing", label: "طلب ونش إنقاذ 🚨" },
        { key: "battery", label: "شحن/استبدال البطارية ⚡" },
        { key: "fuel", label: "توصيل بنزين طارئ ⛽" },
        { key: "pricing", label: "أسعار الخدمات 💰" }
      ]
    : [
        { key: "towing", label: "Request Tow Truck 🚨" },
        { key: "battery", label: "Battery Jumpstart ⚡" },
        { key: "fuel", label: "Emergency Fuel ⛽" },
        { key: "pricing", label: "Service Pricing 💰" }
      ];

  const getBotResponse = (input: string): string => {
    const cleanInput = input.toLowerCase();
    
    if (isRTL) {
      if (cleanInput.includes("ونش") || cleanInput.includes("سحب") || cleanInput.includes("انقاذ") || cleanInput.includes("towing")) {
        return "يمكنك حجز هذه الخدمة عن طريق التطبيق أو عن طريق الواتساب 201096099980";
      }
      if (cleanInput.includes("بطارية") || cleanInput.includes("وصلة") || cleanInput.includes("شحن") || cleanInput.includes("battery")) {
        return "يمكنك حجز هذه الخدمة عن طريق التطبيق أو عن طريق الواتساب 201096099980";
      }
      if (cleanInput.includes("وقود") || cleanInput.includes("بنزين") || cleanInput.includes("fuel")) {
        return "يمكنك حجز هذه الخدمة عن طريق التطبيق أو عن طريق الواتساب 201096099980";
      }
      if (cleanInput.includes("غسيل") || cleanInput.includes("تنظيف") || cleanInput.includes("كار كير") || cleanInput.includes("تلميع") || cleanInput.includes("wash") || cleanInput.includes("clean") || cleanInput.includes("detailing") || cleanInput.includes("polish")) {
        return "خدمات الغسيل والكار كير المتنقل لدينا تشمل:\n\n" +
          "1. باقات الغسيل:\n" +
          "- غسيل خارجي أو داخلي: 200 ج.م.\n" +
          "- الغسيل الكامل (داخلي وخارجي): 350 ج.م.\n\n" +
          "2. باقات الكار كير (تنظيف عميق):\n" +
          "- ميني كار كير: 650 ج.م.\n" +
          "- سوبر كار كير: 950 ج.م.\n" +
          "- توهيلب كار كير المتكامل: 1500 ج.م.\n\n" +
          "3. باقات التلميع والحماية:\n" +
          "- تلميع الفوانيس الأمامية: 600 ج.م.\n" +
          "- بوليش + واكس: 1200 ج.م.\n" +
          "- تجهيز طبقات الحماية: 1800 ج.م.\n" +
          "- إزالة الخدوش وآثار الغسيل: 2500 ج.م.\n\n" +
          "نقوم بالخدمة أمام منزلك أو عملك بمواد ومعدات احترافية. احجز باقتك الآن من النموذج بالأسفل!";
      }
      if (cleanInput.includes("سعر") || cleanInput.includes("اسعار") || cleanInput.includes("أسعار") || cleanInput.includes("أشعار") || cleanInput.includes("تكلف") || cleanInput.includes("بكام") || cleanInput.includes("price") || cleanInput.includes("pricing")) {
        return "أسعار خدماتنا شفافة وتنافسية للغاية:\n\n" +
          "1. خدمات الغسيل والكار كير المتنقل:\n" +
          "- الغسيل (داخلي/خارجي): يبدأ من 200 ج.م (والكامل بـ 350 ج.م).\n" +
          "- الكار كير العميق: يبدأ من 650 ج.م حتى 1500 ج.م للباقة المتكاملة.\n" +
          "- التلميع والحماية: يبدأ من 600 ج.م للفوانيس وحتى 2500 ج.م لمعالجة الخدوش والـ Wax.\n\n" +
          "2. خدمات الإنقاذ والطوارئ:\n" +
          "- ونش الإنقاذ السريع: يبدأ من 150 ج.م (حسب المسافة).\n" +
          "- وصلة البطارية وتوصيل الوقود: تبدأ من 150 ج.م.\n\n" +
          "يمكنك تحديد موقعك وحساب التكلفة الدقيقة فوراً عبر نموذج الحجز بالأسفل دون أي رسوم مخفية!";
      }
      return "مرحباً! لم أفهم سؤالك تماماً، ولكن يمكنك دائماً تصفح الموقع أو الاتصال بالدعم الفني مباشرة عبر الواتساب لتأكيد طلبك \u200E+201096099980";
    } else {
      if (cleanInput.includes("tow") || cleanInput.includes("pull") || cleanInput.includes("rescue") || cleanInput.includes("towing")) {
        return "You can book this service through the app or through WhatsApp +201096099980";
      }
      if (cleanInput.includes("battery") || cleanInput.includes("jump") || cleanInput.includes("start") || cleanInput.includes("charge")) {
        return "You can book this service through the app or through WhatsApp +201096099980";
      }
      if (cleanInput.includes("fuel") || cleanInput.includes("gas") || cleanInput.includes("petrol") || cleanInput.includes("octane")) {
        return "You can book this service through the app or through WhatsApp +201096099980";
      }
      if (cleanInput.includes("wash") || cleanInput.includes("clean") || cleanInput.includes("detail") || cleanInput.includes("polish")) {
        return "Our Mobile Car Wash & Detailing services include:\n\n" +
          "1. Wash Packages:\n" +
          "- Exterior or Interior Wash: 200 EGP.\n" +
          "- Full Wash (Interior & Exterior): 350 EGP.\n\n" +
          "2. Detailing (Deep Clean):\n" +
          "- Mini Car Care: 650 EGP.\n" +
          "- Super Car Care: 950 EGP.\n" +
          "- TowHelp Car Care: 1500 EGP.\n\n" +
          "3. Polish & Protection:\n" +
          "- Headlight Restoration: 600 EGP.\n" +
          "- Polish + Wax: 1200 EGP.\n" +
          "- Ceramic Coating Prep: 1800 EGP.\n" +
          "- Scratch & Swirl Removal: 2500 EGP.\n\n" +
          "We perform the service at your home or office. Book your package via the form below!";
      }
      if (cleanInput.includes("price") || cleanInput.includes("cost") || cleanInput.includes("how much") || cleanInput.includes("fee")) {
        return "Our prices are transparent and highly competitive:\n\n" +
          "1. Mobile Car Wash & Detailing:\n" +
          "- Car Wash (Interior/Exterior): Starts at 200 EGP (Full Wash is 350 EGP).\n" +
          "- Deep Car Care: Starts at 650 EGP up to 1500 EGP.\n" +
          "- Polish & Protection: Starts at 600 EGP up to 2500 EGP.\n\n" +
          "2. Emergency & Towing Services:\n" +
          "- Tow Truck (Winch): Starts at 150 EGP.\n" +
          "- Battery Jumpstart & Fuel Delivery: Start at 150 EGP.\n\n" +
          "You can calculate the exact cost instantly by choosing your service and location in the booking form below!";
      }
      return "Hello! I didn't quite understand your question, but you can always browse the website or contact technical support directly via WhatsApp to confirm your request +201096099980";
    }
  };

  const handleSendMessage = (text: string) => {
    if (!text.trim()) return;

    // Add user message
    const userMsg: Message = {
      id: Date.now(),
      text,
      sender: "user",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue("");
    setIsTyping(true);

    // Simulate bot typing delay
    setTimeout(() => {
      setIsTyping(false);
      const botResponse: Message = {
        id: Date.now() + 1,
        text: getBotResponse(text),
        sender: "bot",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    }, 1200);
  };

  const handleToggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setUnreadCount(0);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: { xs: 20, md: 30 },
        [isRTL ? "right" : "left"]: { xs: 20, md: 30 },
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: isRTL ? "flex-end" : "flex-start",
      }}
    >
      {/* Chat Window Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 25, scale: 0.92 }}
            transition={{ type: "spring", stiffness: 220, damping: 20 }}
            style={{
              marginBottom: "16px",
              width: "calc(100vw - 40px)",
              maxWidth: "380px",
              zIndex: 1001,
            }}
          >
            <Paper
              elevation={8}
              sx={{
                borderRadius: "24px",
                overflow: "hidden",
                border: "1px solid rgba(255, 174, 0, 0.2)",
                background: "linear-gradient(135deg, #0d0d0d 0%, #161616 100%)",
                backdropFilter: "blur(15px)",
                display: "flex",
                flexDirection: "column",
                height: { xs: "480px", sm: "520px" },
                boxShadow: "0 15px 45px rgba(0,0,0,0.7), 0 0 25px rgba(255,174,0,0.06)",
              }}
            >
              {/* Chat Header */}
              <Box
                sx={{
                  p: 2.5,
                  background: "linear-gradient(90deg, #161616 0%, #202020 100%)",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ position: "relative" }}>
                    <Avatar 
                      src={logoEmblem} 
                      alt="TowHelp Emblem"
                      sx={{ 
                        width: 42, 
                        height: 42, 
                        border: "1.5px solid #FFAE00", 
                        backgroundColor: "#0d0d0d",
                        p: 0.5,
                        boxSizing: "border-box",
                        "& img": { objectFit: "contain" }
                      }} 
                    />
                    {/* Active green dot */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 1,
                        right: 1,
                        width: 10,
                        height: 10,
                        backgroundColor: "#4CAF50",
                        borderRadius: "50%",
                        border: "2px solid #161616",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight={800} 
                      sx={{ 
                        color: "#FFAE00", 
                        fontFamily: "Poppins, Ozen",
                        lineHeight: 1.2
                      }}
                    >
                      {isRTL ? "مساعد توهيلب الذكي" : "TowHelp Smart Assistant"}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: "#aaa", 
                        fontFamily: "Poppins", 
                        opacity: 0.8 
                      }}
                    >
                      {isRTL ? "متاح للرد فوراً" : "Online - Instant Reply"}
                    </Typography>
                  </Box>
                </Box>
                <IconButton 
                  onClick={handleToggleChat} 
                  sx={{ 
                    color: "#aaa", 
                    "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.05)" } 
                  }}
                >
                  <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>

              {/* Chat Message History */}
              <Box
                sx={{
                  flex: 1,
                  p: 2.5,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  msOverflowStyle: "none",
                }}
              >
                {messages.map((msg) => (
                  <Box
                    key={msg.id}
                    sx={{
                      display: "flex",
                      justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "80%",
                        p: 1.8,
                        borderRadius: msg.sender === "user" 
                          ? "16px 16px 4px 16px" 
                          : "16px 16px 16px 4px",
                        backgroundColor: msg.sender === "user" 
                          ? "rgba(255, 174, 0, 0.12)" 
                          : "rgba(255, 255, 255, 0.03)",
                        border: msg.sender === "user" 
                          ? "1px solid rgba(255, 174, 0, 0.3)" 
                          : "1px solid rgba(255, 255, 255, 0.06)",
                        color: msg.sender === "user" ? "#FFAE00" : "#ffffff",
                      }}
                    >
                      <Typography
                        variant="body2"
                        dir={isRTL ? "rtl" : "ltr"}
                        sx={{
                          fontFamily: "Poppins, Ozen",
                          fontSize: "0.85rem",
                          lineHeight: 1.5,
                          textAlign: isRTL ? "right" : "left",
                          whiteSpace: "pre-line"
                        }}
                      >
                        {msg.text}
                      </Typography>
                    </Box>
                  </Box>
                ))}

                {/* Typing Indicator */}
                {isTyping && (
                  <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "16px 16px 16px 4px",
                        backgroundColor: "rgba(255, 255, 255, 0.03)",
                        border: "1px solid rgba(255, 255, 255, 0.06)",
                        display: "flex",
                        gap: 0.6,
                        alignItems: "center",
                      }}
                    >
                      {[0, 1, 2].map((i) => (
                        <Box
                          key={i}
                          component={motion.div}
                          animate={{ y: [0, -5, 0] }}
                          transition={{
                            duration: 0.6,
                            repeat: Infinity,
                            delay: i * 0.15,
                            ease: "easeInOut"
                          }}
                          sx={{
                            width: 6,
                            height: 6,
                            backgroundColor: "#FFAE00",
                            borderRadius: "50%",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Quick Replies Panel */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  justifyContent: "center",
                  borderTop: "1px solid rgba(255, 255, 255, 0.03)",
                }}
              >
                {quickReplies.map((reply) => (
                  <Button
                    key={reply.key}
                    onClick={() => handleSendMessage(reply.label)}
                    variant="outlined"
                    sx={{
                      borderRadius: "20px",
                      py: 0.5,
                      px: 2,
                      fontSize: "0.74rem",
                      fontWeight: 650,
                      fontFamily: "Poppins, Ozen",
                      color: "#aaa",
                      borderColor: "rgba(255, 255, 255, 0.12)",
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      "&:hover": {
                        borderColor: "#FFAE00",
                        color: "#FFAE00",
                        backgroundColor: "rgba(255, 174, 0, 0.04)"
                      }
                    }}
                  >
                    {reply.label}
                  </Button>
                ))}
              </Box>

              {/* Chat Input Field */}
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendMessage(inputValue);
                }}
                sx={{
                  p: 2,
                  backgroundColor: "#0d0d0d",
                  borderTop: "1px solid rgba(255, 255, 255, 0.05)",
                  display: "flex",
                  gap: 1,
                  alignItems: "center"
                }}
              >
                <TextField
                  placeholder={isRTL ? "اكتب سؤالك هنا..." : "Type your question..."}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  dir={isRTL ? "rtl" : "ltr"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#fff",
                      borderRadius: "14px",
                      fontFamily: "Poppins, Ozen",
                      fontSize: "0.86rem",
                      backgroundColor: "rgba(255,255,255,0.02)",
                      "& fieldset": { borderColor: "rgba(255, 255, 255, 0.1)" },
                      "&:hover fieldset": { borderColor: "rgba(255, 174, 0, 0.3)" },
                      "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
                    }
                  }}
                />
                <IconButton
                  type="submit"
                  disabled={!inputValue.trim()}
                  sx={{
                    color: "#000",
                    backgroundColor: "#FFAE00",
                    borderRadius: "14px",
                    width: 40,
                    height: 40,
                    "&:hover": {
                      backgroundColor: "#ffc107",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      color: "rgba(255,255,255,0.15)"
                    }
                  }}
                >
                  <SendIcon sx={{ fontSize: 18, transform: isRTL ? "rotate(180deg)" : "none" }} />
                </IconButton>
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Chat Trigger Button */}
      <motion.div
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
      >
        <IconButton
          onClick={handleToggleChat}
          sx={{
            width: { xs: 58, md: 66 },
            height: { xs: 58, md: 66 },
            background: "linear-gradient(135deg, #FFAE00 0%, #FF8F00 100%)",
            color: "#000",
            boxShadow: "0 8px 30px rgba(255, 174, 0, 0.4)",
            position: "relative",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: "linear-gradient(135deg, #ffc107 0%, #FFAE00 100%)",
              boxShadow: "0 12px 35px rgba(255, 174, 0, 0.55)",
            }
          }}
        >
          {isOpen ? (
            <CloseIcon sx={{ fontSize: { xs: 26, md: 30 } }} />
          ) : (
            <>
              <SupportAgentIcon sx={{ fontSize: { xs: 26, md: 30 } }} />
              {/* Unread badge */}
              {unreadCount > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -2,
                    right: -2,
                    backgroundColor: "#FF3B30",
                    color: "#fff",
                    borderRadius: "50%",
                    minWidth: 18,
                    height: 18,
                    fontSize: "0.68rem",
                    fontWeight: "bold",
                    fontFamily: "Poppins",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2.5px solid #080808",
                    px: 0.5
                  }}
                >
                  {unreadCount}
                </Box>
              )}
            </>
          )}
        </IconButton>
      </motion.div>
    </Box>
  );
};

export default InteractiveChatbot;
